import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { financeiroApi, referenciaApi, pedidoApi, recebimentoApi } from '../services/api';
import { PageHeader, KpiCard, Loading, EmptyState, Badge, Modal } from '../components/ui';
import { formatarMoeda, formatarData } from '../lib/format';
import {
  TrendingUp, TrendingDown, Wallet, Search, Plus, Trash2, ReceiptText,
  ArrowUpRight, ArrowDownRight, Sparkles, HandCoins, CheckCircle2,
} from 'lucide-react';

const tipos = [
  { value: '', label: 'Todos os tipos' },
  { value: 'ENTRADA', label: 'Entradas' },
  { value: 'SAIDA', label: 'Saídas' },
];

const areas = [
  { value: '', label: 'Todas as áreas' },
  { value: 'EMPRESA', label: 'Empresa' },
  { value: 'CASA', label: 'Casa' },
];

const formasPagamento = [
  { value: '', label: 'Selecionar...' },
  { value: 'DINHEIRO', label: 'Dinheiro' },
  { value: 'PIX', label: 'PIX' },
  { value: 'CARTAO_DEBITO', label: 'Cartão Débito' },
  { value: 'CARTAO_CREDITO', label: 'Cartão Crédito' },
  { value: 'BOLETO', label: 'Boleto' },
  { value: 'TRANSFERENCIA', label: 'Transferência' },
];

const situacaoFinanceiroMap: Record<string, { tom: string; label: string }> = {
  PENDENTE: { tom: 'amber', label: 'Pendente' },
  RECEBIDO: { tom: 'green', label: 'Recebido' },
  PAGO: { tom: 'green', label: 'Pago' },
  CANCELADO: { tom: 'red', label: 'Cancelado' },
};

export function FinanceiroPage() {
  const [tipo, setTipo] = useState('');
  const [area, setArea] = useState('');
  const [busca, setBusca] = useState('');
  const [modalAberto, setModalAberto] = useState(false);
  const [modalRecebimento, setModalRecebimento] = useState(false);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['financeiro', tipo, area],
    queryFn: () => financeiroApi.listar({ tipo, area }).then(r => r.data),
  });

  const { data: resumoDia } = useQuery({
    queryKey: ['resumo-dia'],
    queryFn: () => financeiroApi.resumoDia().then(r => r.data),
  });

  const invalidarTudo = () => {
    queryClient.invalidateQueries({ queryKey: ['financeiro'] });
    queryClient.invalidateQueries({ queryKey: ['resumo-dia'] });
    queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    queryClient.invalidateQueries({ queryKey: ['pedidos-debitos'] });
    queryClient.invalidateQueries({ queryKey: ['projecao-caixa'] });
  };

  const remover = useMutation({
    mutationFn: (id: string) => financeiroApi.remover(id),
    onSuccess: invalidarTudo,
  });

  // Quitar um lançamento pendente (receber/pagar) direto na tabela
  const quitar = useMutation({
    mutationFn: (l: any) =>
      financeiroApi.atualizar(l.id, { situacao: l.tipo === 'ENTRADA' ? 'RECEBIDO' : 'PAGO' }),
    onSuccess: invalidarTudo,
  });

  const lancamentos = (data?.data || []).filter((l: any) =>
    !busca ||
    l.descricao?.toLowerCase().includes(busca.toLowerCase()) ||
    l.fornecedor?.toLowerCase().includes(busca.toLowerCase()) ||
    l.categoria?.toLowerCase().includes(busca.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="Financeiro"
        descricao="Entradas, saídas e o caixa da operação."
      >
        <button onClick={() => setModalRecebimento(true)} className="btn bg-emerald-600 text-white shadow-sm hover:bg-emerald-700">
          <HandCoins className="w-4 h-4" />
          Novo Recebimento
        </button>
        <button onClick={() => setModalAberto(true)} className="btn-primary">
          <Plus className="w-4 h-4" />
          Novo Lançamento
        </button>
      </PageHeader>

      {/* Resumo do dia */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard titulo="Entradas Hoje" valor={formatarMoeda(resumoDia?.entradas)} icon={TrendingUp} tom="green" />
        <KpiCard titulo="Saídas Hoje" valor={formatarMoeda(resumoDia?.saidas)} icon={TrendingDown} tom="red" />
        <KpiCard titulo="Saldo do Dia" valor={formatarMoeda(resumoDia?.saldo)} icon={Wallet} tom="brand" />
      </div>

      {/* Filtros */}
      <div className="flex flex-col sm:flex-row gap-3">
        <select value={tipo} onChange={e => setTipo(e.target.value)} className="input-field w-full sm:w-48">
          {tipos.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={area} onChange={e => setArea(e.target.value)} className="input-field w-full sm:w-48">
          {areas.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Buscar por descrição, fornecedor ou categoria..."
            value={busca}
            onChange={e => setBusca(e.target.value)}
            className="input-field !pl-10"
          />
        </div>
      </div>

      {/* Tabela */}
      <div className="table-shell">
        {isLoading ? (
          <Loading />
        ) : lancamentos.length === 0 ? (
          <EmptyState
            icon={ReceiptText}
            titulo="Nenhum lançamento encontrado"
            descricao="Ajuste os filtros ou registre um novo lançamento."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Descrição</th>
                  <th>Categoria</th>
                  <th>Área</th>
                  <th className="!text-right">Valor</th>
                  <th>Situação</th>
                  <th className="!text-center w-16"></th>
                </tr>
              </thead>
              <tbody>
                {lancamentos.map((l: any) => {
                  const s = situacaoFinanceiroMap[l.situacao] || { tom: 'gray', label: l.situacao };
                  return (
                    <tr key={l.id}>
                      <td className="text-ink-500 whitespace-nowrap">{formatarData(l.data)}</td>
                      <td>
                        <p className="font-medium text-ink-800">{l.descricao}</p>
                        <p className="text-xs text-ink-400 mt-0.5 flex items-center gap-1.5">
                          {l.fornecedor && <span>{l.fornecedor}</span>}
                          {l.pedido && (
                            <span className="badge bg-brand-50 text-brand-700 ring-1 ring-inset ring-brand-600/20">
                              Pedido #{l.pedido.numero}
                            </span>
                          )}
                          {l.vencimento && l.situacao === 'PENDENTE' && (
                            <span>vence {formatarData(l.vencimento)}</span>
                          )}
                        </p>
                      </td>
                      <td>
                        <span className="px-2.5 py-1 bg-ink-100 rounded-lg text-2xs font-semibold text-ink-600 uppercase tracking-wide">
                          {l.categoria}
                        </span>
                      </td>
                      <td>
                        <Badge tom={l.area === 'EMPRESA' ? 'blue' : 'amber'} label={l.area === 'EMPRESA' ? 'Empresa' : 'Casa'} />
                      </td>
                      <td className={`!text-right font-semibold whitespace-nowrap ${l.tipo === 'ENTRADA' ? 'text-emerald-600' : 'text-red-600'}`}>
                        {l.tipo === 'ENTRADA' ? '+' : '−'} {formatarMoeda(l.valor)}
                      </td>
                      <td><Badge tom={s.tom} label={s.label} /></td>
                      <td>
                        <div className="flex justify-center gap-1">
                          {l.situacao === 'PENDENTE' && (
                            <button
                              onClick={() => quitar.mutate(l)}
                              className="icon-btn hover:!bg-emerald-50 hover:!text-emerald-600"
                              title={l.tipo === 'ENTRADA' ? 'Marcar como recebido' : 'Marcar como pago'}
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => {
                              if (window.confirm(`Remover "${l.descricao}"?`)) remover.mutate(l.id);
                            }}
                            className="icon-btn hover:!bg-red-50 hover:!text-red-600"
                            title="Remover lançamento"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ModalNovoLancamento aberto={modalAberto} onFechar={() => setModalAberto(false)} />
      <ModalNovoRecebimento aberto={modalRecebimento} onFechar={() => setModalRecebimento(false)} />
    </div>
  );
}

/* ── Modal: Novo Recebimento (baixa de pedido ou crédito) ── */
function ModalNovoRecebimento({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [vincular, setVincular] = useState(true);
  const [pedidoId, setPedidoId] = useState('');
  const [valor, setValor] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('');
  const [dataRecebimento, setDataRecebimento] = useState(() => new Date().toISOString().split('T')[0]);
  const [descricao, setDescricao] = useState('');
  const [categoria, setCategoria] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState('');

  // Pedidos com débito para escolher a baixa
  const { data: debitos } = useQuery({
    queryKey: ['pedidos-debitos', 'select'],
    queryFn: () => pedidoApi.debitos({ limit: 500 }).then(r => r.data),
    enabled: aberto && vincular,
  });

  const pedidoSelecionado = (debitos?.data || []).find((p: any) => p.id === pedidoId);

  const resetar = () => {
    setVincular(true);
    setPedidoId('');
    setValor('');
    setFormaPagamento('');
    setDataRecebimento(new Date().toISOString().split('T')[0]);
    setDescricao('');
    setCategoria('');
    setObservacoes('');
    setErro('');
  };

  const criar = useMutation({
    mutationFn: (dados: any) => recebimentoApi.criar(dados),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro'] });
      queryClient.invalidateQueries({ queryKey: ['resumo-dia'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-debitos'] });
      queryClient.invalidateQueries({ queryKey: ['projecao-caixa'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      resetar();
      onFechar();
    },
    onError: (err: any) => {
      setErro(err.response?.data?.error || 'Não foi possível registrar o recebimento.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    const valorNum = parseFloat(valor);
    if (!valorNum || valorNum <= 0) { setErro('Informe um valor maior que zero.'); return; }

    if (vincular) {
      if (!pedidoId) { setErro('Selecione o pedido que está sendo baixado.'); return; }
      criar.mutate({
        pedidoId,
        valor: valorNum,
        formaPagamento: formaPagamento || undefined,
        data: new Date(`${dataRecebimento}T12:00:00`).toISOString(),
        observacoes: observacoes.trim() || undefined,
      });
    } else {
      if (!descricao.trim()) { setErro('Descreva a origem do crédito.'); return; }
      criar.mutate({
        valor: valorNum,
        descricao: descricao.trim(),
        categoria: categoria.trim() || 'Crédito Avulso',
        formaPagamento: formaPagamento || undefined,
        data: new Date(`${dataRecebimento}T12:00:00`).toISOString(),
        observacoes: observacoes.trim() || undefined,
      });
    }
  };

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo="Novo Recebimento"
      descricao="Vincule a um pedido para dar baixa (total ou parcial) ou registre um crédito avulso."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Vínculo */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-ink-100 rounded-xl">
          <button
            type="button"
            onClick={() => setVincular(true)}
            className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${
              vincular ? 'bg-brand-600 text-white shadow-sm' : 'text-ink-500 hover:text-ink-800'
            }`}
          >
            Baixa de Pedido
          </button>
          <button
            type="button"
            onClick={() => setVincular(false)}
            className={`py-2.5 rounded-lg text-sm font-semibold transition-all ${
              !vincular ? 'bg-brand-600 text-white shadow-sm' : 'text-ink-500 hover:text-ink-800'
            }`}
          >
            Crédito Avulso
          </button>
        </div>

        {vincular ? (
          <div>
            <label className="input-label">Pedido com débito *</label>
            <select
              value={pedidoId}
              onChange={e => {
                setPedidoId(e.target.value);
                const p = (debitos?.data || []).find((x: any) => x.id === e.target.value);
                if (p) setValor(String(p.saldoDevedor));
              }}
              className="input-field"
              required
            >
              <option value="">Selecionar pedido...</option>
              {(debitos?.data || []).map((p: any) => (
                <option key={p.id} value={p.id}>
                  #{p.numero} — {p.cliente?.nome} (deve {formatarMoeda(p.saldoDevedor)})
                </option>
              ))}
            </select>
            {pedidoSelecionado && (
              <p className="text-2xs text-ink-500 mt-1.5">
                Total {formatarMoeda(pedidoSelecionado.valorTotal)}
                {pedidoSelecionado.valorRecebido > 0 && ` · já recebido ${formatarMoeda(pedidoSelecionado.valorRecebido)}`}
                {' · '}saldo <strong className="text-red-600">{formatarMoeda(pedidoSelecionado.saldoDevedor)}</strong>
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="input-label">Descrição / Origem *</label>
              <input
                value={descricao}
                onChange={e => setDescricao(e.target.value)}
                placeholder="Ex: Venda avulsa, reembolso..."
                className="input-field"
              />
            </div>
            <div>
              <label className="input-label">Categoria</label>
              <input
                value={categoria}
                onChange={e => setCategoria(e.target.value)}
                placeholder="Crédito Avulso"
                className="input-field"
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="input-label">Valor *</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-ink-400">R$</span>
              <input
                type="number" step="0.01" min="0.01"
                value={valor} onChange={e => setValor(e.target.value)}
                placeholder="0,00" className="input-field !pl-10" required
              />
            </div>
            {vincular && pedidoSelecionado && parseFloat(valor) > 0 && parseFloat(valor) < pedidoSelecionado.saldoDevedor - 0.009 && (
              <p className="text-2xs font-medium text-blue-600 mt-1.5">
                Baixa parcial — restarão {formatarMoeda(pedidoSelecionado.saldoDevedor - parseFloat(valor))}
              </p>
            )}
          </div>
          <div>
            <label className="input-label">Data</label>
            <input type="date" value={dataRecebimento} onChange={e => setDataRecebimento(e.target.value)} className="input-field" />
          </div>
        </div>

        <div>
          <label className="input-label">Forma de Pagamento</label>
          <select value={formaPagamento} onChange={e => setFormaPagamento(e.target.value)} className="input-field">
            {formasPagamento.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        <div>
          <label className="input-label">Observações</label>
          <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} rows={2} className="input-field" placeholder="Opcional" />
        </div>

        {erro && (
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700">{erro}</div>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
          <button type="submit" disabled={criar.isPending} className="btn bg-emerald-600 text-white shadow-sm hover:bg-emerald-700">
            {criar.isPending ? 'Registrando...' : vincular ? 'Registrar Baixa' : 'Registrar Crédito'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ── Modal: Novo Lançamento ─────────────────────────────── */
function ModalNovoLancamento({ aberto, onFechar }: { aberto: boolean; onFechar: () => void }) {
  const queryClient = useQueryClient();
  const [tipo, setTipo] = useState<'ENTRADA' | 'SAIDA'>('SAIDA');
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [categoria, setCategoria] = useState('');
  const [area, setArea] = useState<'EMPRESA' | 'CASA'>('EMPRESA');
  const [situacao, setSituacao] = useState<'PENDENTE' | 'RECEBIDO' | 'PAGO'>('PAGO');
  const [formaPagamento, setFormaPagamento] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  const [dataLancamento, setDataLancamento] = useState(() => new Date().toISOString().split('T')[0]);
  const [vencimento, setVencimento] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [categoriaEditada, setCategoriaEditada] = useState(false);
  const [sugestoesCategoria, setSugestoesCategoria] = useState<string[]>([]);
  const [erro, setErro] = useState('');

  const resetar = () => {
    setTipo('SAIDA');
    setDescricao('');
    setValor('');
    setCategoria('');
    setArea('EMPRESA');
    setSituacao('PAGO');
    setFormaPagamento('');
    setFornecedor('');
    setDataLancamento(new Date().toISOString().split('T')[0]);
    setVencimento('');
    setObservacoes('');
    setCategoriaEditada(false);
    setSugestoesCategoria([]);
    setErro('');
  };

  const criar = useMutation({
    mutationFn: (data: any) => financeiroApi.criar(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['financeiro'] });
      queryClient.invalidateQueries({ queryKey: ['resumo-dia'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      resetar();
      onFechar();
    },
    onError: (err: any) => {
      setErro(err.response?.data?.error || 'Não foi possível salvar. Verifique os campos.');
    },
  });

  // Sugestão inteligente: ao sair da descrição, o backend infere categoria/área/tipo
  const sugerirPorDescricao = async () => {
    if (descricao.trim().length < 3) return;
    try {
      const res = await financeiroApi.sugerir(descricao.trim());
      if (res.data) {
        if (!categoriaEditada && res.data.categoria) setCategoria(res.data.categoria);
        if (res.data.area) setArea(res.data.area);
      }
    } catch {
      /* sugestão é opcional */
    }
  };

  const buscarCategorias = async (q: string) => {
    if (q.length < 2) { setSugestoesCategoria([]); return; }
    try {
      const res = await referenciaApi.sugerir('CATEGORIA_FINANCEIRA', q);
      setSugestoesCategoria(res.data || []);
    } catch {
      setSugestoesCategoria([]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErro('');
    const valorNum = parseFloat(valor);
    if (!descricao.trim() || !categoria.trim() || !valorNum || valorNum <= 0) {
      setErro('Preencha descrição, categoria e um valor maior que zero.');
      return;
    }
    criar.mutate({
      tipo,
      area,
      descricao: descricao.trim(),
      categoria: categoria.trim(),
      valor: valorNum,
      data: new Date(`${dataLancamento}T12:00:00`).toISOString(),
      vencimento: vencimento ? new Date(`${vencimento}T12:00:00`).toISOString() : undefined,
      situacao,
      formaPagamento: formaPagamento || undefined,
      fornecedor: fornecedor.trim() || undefined,
      observacoes: observacoes.trim() || undefined,
    });
  };

  const selecionarTipo = (t: 'ENTRADA' | 'SAIDA') => {
    setTipo(t);
    setSituacao(t === 'ENTRADA' ? 'RECEBIDO' : 'PAGO');
  };

  return (
    <Modal
      aberto={aberto}
      onFechar={onFechar}
      titulo="Novo Lançamento"
      descricao="Registre uma entrada ou saída no caixa."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Tipo */}
        <div className="grid grid-cols-2 gap-2 p-1 bg-ink-100 rounded-xl">
          <button
            type="button"
            onClick={() => selecionarTipo('ENTRADA')}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              tipo === 'ENTRADA'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-ink-500 hover:text-ink-800'
            }`}
          >
            <ArrowUpRight className="w-4 h-4" /> Entrada
          </button>
          <button
            type="button"
            onClick={() => selecionarTipo('SAIDA')}
            className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${
              tipo === 'SAIDA'
                ? 'bg-red-600 text-white shadow-sm'
                : 'text-ink-500 hover:text-ink-800'
            }`}
          >
            <ArrowDownRight className="w-4 h-4" /> Saída
          </button>
        </div>

        {/* Descrição */}
        <div>
          <label className="input-label" htmlFor="lanc-desc">Descrição *</label>
          <input
            id="lanc-desc"
            type="text"
            value={descricao}
            onChange={e => setDescricao(e.target.value)}
            onBlur={sugerirPorDescricao}
            placeholder={tipo === 'ENTRADA' ? 'Ex: Venda de 10 camisetas' : 'Ex: Compra de tecido, mercado...'}
            className="input-field"
            required
            autoFocus
          />
          <p className="flex items-center gap-1.5 text-2xs text-ink-400 mt-1.5">
            <Sparkles className="w-3 h-3" />
            Categoria e área são sugeridas automaticamente pela descrição.
          </p>
        </div>

        {/* Valor + Data */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="input-label" htmlFor="lanc-valor">Valor *</label>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-ink-400">R$</span>
              <input
                id="lanc-valor"
                type="number"
                step="0.01"
                min="0.01"
                value={valor}
                onChange={e => setValor(e.target.value)}
                placeholder="0,00"
                className="input-field !pl-10"
                required
              />
            </div>
          </div>
          <div>
            <label className="input-label" htmlFor="lanc-data">Data</label>
            <input
              id="lanc-data"
              type="date"
              value={dataLancamento}
              onChange={e => setDataLancamento(e.target.value)}
              className="input-field"
            />
          </div>
        </div>

        {/* Categoria + Área */}
        <div className="grid grid-cols-2 gap-4">
          <div className="relative">
            <label className="input-label" htmlFor="lanc-cat">Categoria *</label>
            <input
              id="lanc-cat"
              type="text"
              value={categoria}
              onChange={e => {
                setCategoria(e.target.value);
                setCategoriaEditada(true);
                buscarCategorias(e.target.value);
              }}
              onBlur={() => setTimeout(() => setSugestoesCategoria([]), 200)}
              placeholder="Ex: Tecido/Malha"
              className="input-field"
              required
            />
            {sugestoesCategoria.length > 0 && (
              <ul className="absolute z-10 w-full bg-white border border-ink-200 rounded-xl shadow-pop mt-1 max-h-36 overflow-auto animate-fade-in">
                {sugestoesCategoria.map(s => (
                  <li
                    key={s}
                    onClick={() => { setCategoria(s); setCategoriaEditada(true); setSugestoesCategoria([]); }}
                    className="px-3.5 py-2 hover:bg-brand-50 cursor-pointer text-sm text-ink-700"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label className="input-label">Área</label>
            <div className="grid grid-cols-2 gap-2">
              {(['EMPRESA', 'CASA'] as const).map(a => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setArea(a)}
                  className={`py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                    area === a
                      ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                      : 'bg-white text-ink-500 border-ink-200 hover:border-ink-300'
                  }`}
                >
                  {a === 'EMPRESA' ? 'Empresa' : 'Casa'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Situação + Pagamento */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="input-label" htmlFor="lanc-sit">Situação</label>
            <select
              id="lanc-sit"
              value={situacao}
              onChange={e => setSituacao(e.target.value as any)}
              className="input-field"
            >
              {tipo === 'ENTRADA' ? (
                <>
                  <option value="RECEBIDO">Recebido</option>
                  <option value="PENDENTE">Pendente</option>
                </>
              ) : (
                <>
                  <option value="PAGO">Pago</option>
                  <option value="PENDENTE">Pendente</option>
                </>
              )}
            </select>
          </div>
          <div>
            <label className="input-label" htmlFor="lanc-pag">Forma de Pagamento</label>
            <select
              id="lanc-pag"
              value={formaPagamento}
              onChange={e => setFormaPagamento(e.target.value)}
              className="input-field"
            >
              {formasPagamento.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Fornecedor */}
        <div>
          <label className="input-label" htmlFor="lanc-forn">
            {tipo === 'ENTRADA' ? 'Cliente / Origem' : 'Fornecedor'}
          </label>
          <input
            id="lanc-forn"
            type="text"
            value={fornecedor}
            onChange={e => setFornecedor(e.target.value)}
            placeholder="Opcional"
            className="input-field"
          />
        </div>

        {/* Vencimento (para contas pendentes — alimenta a projeção de caixa) */}
        {situacao === 'PENDENTE' && (
          <div>
            <label className="input-label" htmlFor="lanc-venc">Vencimento</label>
            <input
              id="lanc-venc"
              type="date"
              value={vencimento}
              onChange={e => setVencimento(e.target.value)}
              className="input-field"
            />
          </div>
        )}

        {/* Observações */}
        <div>
          <label className="input-label" htmlFor="lanc-obs">Observações</label>
          <textarea
            id="lanc-obs"
            value={observacoes}
            onChange={e => setObservacoes(e.target.value)}
            rows={2}
            placeholder="Opcional"
            className="input-field"
          />
        </div>

        {erro && (
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700 animate-fade-in">
            {erro}
          </div>
        )}

        <div className="flex justify-end gap-3 pt-1">
          <button type="button" onClick={onFechar} className="btn-secondary">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={criar.isPending}
            className={tipo === 'ENTRADA' ? 'btn bg-emerald-600 text-white shadow-sm hover:bg-emerald-700' : 'btn-primary'}
          >
            {criar.isPending ? 'Salvando...' : tipo === 'ENTRADA' ? 'Salvar Entrada' : 'Salvar Saída'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
